import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { analyticsMFACloseButton, analyticsPage } from '../../../api/services/Analytics';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { LoginForm } from '../../features/Login/LoginForm';
import { LoginTemplate, LoginTemplateV2 } from '../../features/Login/LoginTemplate';
import { MultiFactorAuth } from '../../features/Login/MultiFactorAuth';
import { MFAMethodSelection } from '../../features/Login/MultiFactorAuth/MFAMethodSelection';
import { PendingApprovalModal } from '../../features/Login/PendingApprovalModal/PendingApprovalModal';
import { SSOLoginForm } from '../../features/Login/SSOLoginForm';
import { useAuthMutations } from '../../hooks/useAuthMutations';
import { useMFAChoiceAnalytics } from '../../hooks/useMFAChoiceAnalytics';

const Login: React.FC = () => {
  const {
    handleLogin,
    handleMFA,
    loginErrors,
    mfaErrors,
    isAccountLocked,
    isVerifying,
    showMFAModal,
    setShowMFAModal,
    userEmail,
    mfaStep,
    sendingMethod,
    methodErrors,
    selectMFAMethod,
    resendMFACode,
    canChooseMFAMethod,
    isDeliveryUnavailable,
    returnToMFAMethodChoice,
    sendCooldownSeconds,
    methodCooldownSeconds,
    codeSent,
  } = useAuthMutations({ includeSessionErrors: true });
  const methodChoice = mfaStep?.mfa?.state === 'choose_method' ? mfaStep.mfa : undefined;
  const delivery = mfaStep?.mfa?.state === 'code_sent' ? mfaStep.mfa : undefined;
  const hasSentCode = !!delivery || (mfaStep?.message === 'Provide OTP code.' && !mfaStep.mfa);
  let mfaViewState: 'none' | 'choice' | 'code' = 'none';

  if (methodChoice) {
    mfaViewState = 'choice';
  } else if (hasSentCode) {
    mfaViewState = 'code';
  }

  useMFAChoiceAnalytics(mfaViewState);
  const navigate = useNavigate();
  const { addToast } = useToastStore();
  const { ssoEnabled, redesignedLoginPageEnabled } = useFlags();
  const Template = redesignedLoginPageEnabled ? LoginTemplateV2 : LoginTemplate;
  const closeMFAChoice = () => {
    setShowMFAModal(false);
    analyticsMFACloseButton(userEmail, false, false);
  };

  useEffect(() => {
    const toastMessages = JSON.parse(window.sessionStorage.getItem('toastMessages') || '{}');
    const { severity, messages } = toastMessages;

    if (!!messages) {
      messages.forEach((message) => addToast({ severity: severity, message: message }));
      window.sessionStorage.removeItem('toastMessages');
    }
    analyticsPage('Login');
  }, []);

  return (
    <Template>
      {ssoEnabled ? (
        <SSOLoginForm
          handleSignIn={handleLogin}
          handleForgotPassword={() => navigate('/forgotPassword')}
          serverErrors={loginErrors}
        />
      ) : (
        <LoginForm
          serverErrors={loginErrors}
          handleSignIn={handleLogin}
          handleForgotPassword={() => navigate('/forgotPassword')}
          validationSchema={schema}
        />
      )}
      {showMFAModal && methodChoice ? (
        <MFAMethodSelection
          methods={methodChoice.methods}
          onSelect={selectMFAMethod}
          onClose={closeMFAChoice}
          sendingMethod={sendingMethod}
          errors={methodErrors}
          methodCooldownSeconds={methodCooldownSeconds}
        />
      ) : null}
      {showMFAModal && hasSentCode && (
        <MultiFactorAuth
          key={delivery ? `${delivery.method}:${delivery.destination}` : 'legacy-email'}
          isVerifying={isVerifying}
          serverErrors={mfaErrors}
          handleMFAVerify={handleMFA}
          resendMFACode={resendMFACode}
          deliveryErrors={methodErrors}
          onChooseMethod={canChooseMFAMethod ? returnToMFAMethodChoice : undefined}
          sendCooldownSeconds={sendCooldownSeconds}
          codeSent={codeSent}
          isDeliveryUnavailable={isDeliveryUnavailable}
          isLocked={isAccountLocked}
          showMFA={showMFAModal}
          setShowMFA={setShowMFAModal}
          userEmail={userEmail}
          delivery={delivery}
        />
      )}
      <PendingApprovalModal />
    </Template>
  );
};

export const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(8).max(32).required(),
});
export default Login;
